<template>
  <div class="scratchcards-page pt-0">
    <NuxtLayout name="teasers" v-bind="{ loading }" :no-results="!teasers.length">
      <component
        :is="component"
        v-for="({ component, element, key, props }, index) in items"
        v-bind="{ key, ...props }"
        :fetchpriority="index < priorityFetchAmount ? 'high' : 'low'"
        @click="handleTeaserClick(element)"
      />

      <template #no-results>
        <LazyIllustratedSection empty illustration="sold-out" outlined>
          <template #headline>{{ $t('home.tab.scratch.noresults') }}</template>
        </LazyIllustratedSection>
      </template>
    </NuxtLayout>

    <HighFrequencyGameExploreMoreCarousel :variants="['for-you', 'draw', 'keno']" />
  </div>
</template>

<script lang="ts" setup>
import type {
  FreeToPlayTeaserPayload,
  RecommendationElement,
  ScratchcardTeaserPayload,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import { FREE_TO_PLAY_ITEM_NAME } from '#free-to-play/app/constants/constants'

const { locale } = useI18n()
const licensedTerritory = useLicensedTerritory({ raw: true })
const { isDesktop } = useDevice()

const { filteredTeasers, loading } = useScratchcardFilter({ eager: true })

const priorityFetchAmount = computed(() => (isDesktop ? 6 : 3))

const TRACKING_LIST_NAME = 'Home Scratchcard Teasers'
const TRACKING_LIST_ID = 'home_scratchcard_teasers'

const { createTeaserListItems } = useTrackingData()
const { pushSelectListItemEvent, pushViewListEvent } = useTracking()
const { logTrackingError } = useTrackingErrorLog()

const trackingTeaserListItems = ref<ReturnType<typeof createTeaserListItems>>([])

const teasers = computed(
  () => filteredTeasers.value as (FreeToPlayTeaserPayload | ScratchcardTeaserPayload)[],
)

const items = computed(() =>
  mapTeaserElements(teasers.value, {
    licensedTerritory,
    locale: locale.value,
  }),
)

const handleTeaserClick = (teaser: RecommendationElement) => {
  const teaserItem = trackingTeaserListItems.value.find((item) => {
    switch (teaser.__typename) {
      case 'FreeToPlayTeaserPayload':
        return item.item_name === FREE_TO_PLAY_ITEM_NAME
      case 'ScratchcardTeaserPayload':
        return item.item_name === teaser.name
      default:
        return false
    }
  })

  if (!teaserItem) return

  try {
    pushSelectListItemEvent({
      items: [teaserItem],
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })
  } catch (err) {
    logTrackingError('select_list_item', err)
  }
}

let hasTracked = false
let unwatchTeasers: Maybe<() => void> = null
unwatchTeasers = watchImmediate(
  () => teasers.value,
  (list) => {
    if (!list || list.length === 0) return

    if (!hasTracked) {
      hasTracked = true

      try {
        trackingTeaserListItems.value = createTeaserListItems(list)
        pushViewListEvent({
          items: trackingTeaserListItems.value,
          list_id: TRACKING_LIST_ID,
          list_name: TRACKING_LIST_NAME,
        })
      } catch (err) {
        logTrackingError('view_list', err)
      }
    }

    if (unwatchTeasers) unwatchTeasers()
  },
)
</script>

<style lang="scss" scoped>
.scratchcards-page :deep(.teasers) {
  padding-bottom: token('size/space/gap/200');
}
</style>
